import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check } from 'lucide-react';
import {
  useActivateCompetition,
  useCompetition,
  useDeleteCompetition,
  useSetFzsPatronage,
  useScheduleCompetition,
  useUpdateCompetition,
  useUploadCompetitionLogo,
  useCompetitionIdParam,
} from '@/hooks/useCompetitions';
import { useCategories } from '@/hooks/useCategories';
import { usePaymentSettings, useSetEntryFee } from '@/hooks/usePayments';
import { centsToEuroText, parseEuroToCents } from '@/lib/payments';
import { useJudgesForCompetition } from '@/hooks/useJudging';
import { PageSpinner } from '@/components/ui/Spinner';
import { Alert, ErrorState } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { DateField } from '@/components/ui/DateField';
import { CompetitionWorkspaceHeader } from '@/components/organizer/CompetitionWorkspace';

import { getApiErrorMessage } from '@/api/client';
import { CompetitionStatus, type Competition } from '@/api/types';
import { competitionStatusLabel, competitionStatusTone, isCompetitionEditable } from '@/lib/competitions';
import {
  fromDateInputValue,
  fromDateTimeInputValue,
  toDateInputValue,
  toDateTimeInputValue,
} from '@/lib/dateInputs';
import { OrganizerCategoriesPage } from './OrganizerCategoriesPage';
import { OrganizerLandingPage } from './OrganizerLandingPage';
import { OrganizerJudgesPage } from './OrganizerJudgesPage';
import { OrganizerAwardsPage } from './OrganizerAwardsPage';

const STEPS = [
  { key: 'basics', label: 'Osnovno' },
  { key: 'categories', label: 'Kategorije' },
  { key: 'announcement', label: 'Razpis in zahteve' },
  { key: 'judges', label: 'Sodniki' },
  { key: 'awards', label: 'Nagrade' },
  { key: 'publish', label: 'Pregled in objava' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

function BasicsStep({ competition }: { competition: Competition }) {
  const update = useUpdateCompetition(competition.id);
  const uploadLogo = useUploadCompetitionLogo(competition.id);
  const setFzs = useSetFzsPatronage(competition.id);
  const payment = usePaymentSettings(competition.id);
  const setEntryFee = useSetEntryFee(competition.id);
  const [fee, setFee] = useState<string | null>(null);
  const [feeError, setFeeError] = useState<string | undefined>();
  const [feeSaved, setFeeSaved] = useState(false);
  const feeValue = fee ?? centsToEuroText(payment.data?.entryFeeCents ?? null);

  async function saveFee() {
    const cents = parseEuroToCents(feeValue);
    if (Number.isNaN(cents)) {
      setFeeError('Vnesite znesek, npr. 15 ali 12,50.');
      return;
    }
    setFeeError(undefined);
    await setEntryFee.mutateAsync(cents);
    setFee(null);
    setFeeSaved(true);
  }
  const deleteCompetition = useDeleteCompetition();
  const navigate = useNavigate();

  function handleDelete() {
    if (!confirm(`Izbriši "${competition.name}"?`)) return;
    deleteCompetition.mutate(
      { id: competition.id },
      {
        onSuccess: () => navigate('/organizer/competitions'),
        onError: (error) => {
          // Entries block a plain delete; the organizer may still force it after a second, explicit confirmation.
          if (!axios.isAxiosError(error) || !error.response?.data?.errors?.id) return;
          if (confirm(`"${competition.name}" ima oddane prispevke. Z izbrisom se trajno izbrišejo tudi vsi prispevki, ocene in nagrade. Tega ni mogoče razveljaviti. Vseeno izbrišem?`)) {
            deleteCompetition.mutate({ id: competition.id, force: true }, { onSuccess: () => navigate('/organizer/competitions') });
          }
        },
      },
    );
  }
  const editable = isCompetitionEditable(competition.status);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: competition.name,
    description: competition.description ?? '',
    submissionOpensAt: toDateInputValue(competition.submissionOpensAt),
    submissionDeadline: toDateTimeInputValue(competition.submissionDeadline),
    judgingDeadline: toDateTimeInputValue(competition.judgingDeadline),
    startDate: toDateInputValue(competition.startDate),
    endDate: toDateInputValue(competition.endDate),
  });
  const setValue = (key: keyof typeof form) => (value: string) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: value }));
  };
  const set = (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setValue(key)(e.target.value);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      name: form.name,
      description: form.description || undefined,
      submissionOpensAt: fromDateInputValue(form.submissionOpensAt),
      submissionDeadline: fromDateTimeInputValue(form.submissionDeadline),
      judgingDeadline: fromDateTimeInputValue(form.judgingDeadline),
      startDate: fromDateInputValue(form.startDate),
      endDate: fromDateInputValue(form.endDate),
    });
    setSaved(true);
  }

  async function handleLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadLogo.mutateAsync(file);
    e.target.value = '';
  }

  return (
    <Card>
      <CardBody>
        <form onSubmit={handleSubmit} className="flex max-w-2xl flex-col gap-3">
          {update.isError && <Alert variant="error">{getApiErrorMessage(update.error)}</Alert>}
          {!editable && <Alert variant="info">Zaključenega tekmovanja ni več mogoče urejati.</Alert>}
          <Input label="Naziv" value={form.name} onChange={set('name')} required disabled={!editable} />
          <Input label="Opis" value={form.description} onChange={set('description')} disabled={!editable} />
          <div className="grid gap-3 sm:grid-cols-2">
            <DateField label="Odprtje za oddajo" value={form.submissionOpensAt} onChange={setValue('submissionOpensAt')} disabled={!editable} />
            <DateField label="Rok za oddajo (datum in ura)" withTime value={form.submissionDeadline} onChange={setValue('submissionDeadline')} disabled={!editable} />
            <DateField label="Rok za ocenjevanje (datum in ura)" withTime value={form.judgingDeadline} onChange={setValue('judgingDeadline')} disabled={!editable} />
            <span className="hidden sm:block" />
            <DateField label="Datum začetka" value={form.startDate} onChange={setValue('startDate')} disabled={!editable} />
            <DateField label="Datum konca" value={form.endDate} onChange={setValue('endDate')} disabled={!editable} />
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" isLoading={update.isPending} disabled={!editable}>
              Shrani
            </Button>
            {saved && <span className="text-sm text-success">Shranjeno.</span>}
          </div>

          <div className="flex flex-col gap-2 border-t border-line pt-3">
            {uploadLogo.isError && <Alert variant="error">{getApiErrorMessage(uploadLogo.error)}</Alert>}
            <div className="flex items-center gap-3">
              {competition.logoUrl ? (
                <img src={competition.logoUrl} alt={`${competition.name} logo`} className="h-12 w-12 rounded-photo border border-line object-contain" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-photo border border-dashed border-line-strong text-xs text-fg-muted">
                  Brez logotipa
                </div>
              )}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-fg-secondary">
                  Logotip tekmovanja{' '}
                  <span className="font-normal text-fg-muted">
                    (neobvezno; slika na kartici tekmovanja ob prijavi in na razpisu, sicer se uporabi logotip organizacije)
                  </span>
                </label>
                <p className="text-xs text-fg-muted">
                  PNG, JPG ali WebP · 200–2000 px na stranico · do 2 MB · priporočeno kvadratno, npr. 512 × 512 px, PNG s prosojnim ozadjem.
                </p>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={uploadLogo.isPending}
                  onChange={handleLogo}
                  className="text-sm text-fg-secondary file:mr-3 file:h-8 file:rounded-control file:border file:border-line-strong file:bg-surface-2 file:px-3 file:text-[13px] file:font-medium file:text-fg hover:file:bg-surface-3"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-line pt-3">
            <p className="text-sm font-medium text-fg">Kotizacija</p>
            <p className="text-sm text-fg-muted">
              Neobvezno. Dokler zneska ni, tekmovalci plačil ne vidijo. Podatke kluba za nakazilo in Stripe nastavite v meniju pri{' '}
              <Link to={`/organizer/organizations/${competition.organizationId}/payments`} className="font-medium text-accent-text underline">
                Plačila
              </Link>
              .
            </p>
            {setEntryFee.isError && <Alert variant="error">{getApiErrorMessage(setEntryFee.error)}</Alert>}
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-40">
                <Input
                  label="Znesek v evrih"
                  inputMode="decimal"
                  placeholder="npr. 15,00"
                  value={feeValue}
                  onChange={(e) => {
                    setFee(e.target.value);
                    setFeeSaved(false);
                  }}
                  error={feeError}
                  disabled={payment.isLoading}
                />
              </div>
              <Button type="button" variant="secondary" isLoading={setEntryFee.isPending} onClick={saveFee}>
                Shrani kotizacijo
              </Button>
              {feeSaved && <span className="text-sm text-success">Shranjeno.</span>}
            </div>
          </div>

          <div className="flex flex-col gap-2 border-t border-line pt-3">
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={competition.isFzsPatronage}
                disabled={setFzs.isPending}
                onChange={(e) => setFzs.mutate(e.target.checked)}
              />
              Pod pokroviteljstvom FZS (Fotografske zveze Slovenije)
            </label>
            {setFzs.isError && <Alert variant="error">{getApiErrorMessage(setFzs.error)}</Alert>}
          </div>

          <div className="flex flex-col gap-2 border-t border-line pt-3">
            {deleteCompetition.isError && <Alert variant="error">{getApiErrorMessage(deleteCompetition.error)}</Alert>}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-fg-muted">Izbris tekmovanja je trajen; s prispevki vas program vpraša še enkrat.</p>
              <Button type="button" variant="danger" size="sm" isLoading={deleteCompetition.isPending} onClick={handleDelete}>
                Izbriši tekmovanje
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function PublishStep({ competition, goTo }: { competition: Competition; goTo: (k: StepKey) => void }) {
  const categories = useCategories(competition.id);
  const judges = useJudgesForCompetition(competition.id);
  const schedule = useScheduleCompetition();
  const activate = useActivateCompetition();

  const checks: { ok: boolean; label: string; step: StepKey }[] = [
    { ok: Boolean(competition.submissionDeadline), label: 'Rok za oddajo je določen', step: 'basics' },
    { ok: (categories.data?.items.length ?? 0) > 0, label: 'Vsaj ena kategorija', step: 'categories' },
    { ok: (judges.data?.items.length ?? 0) > 0, label: 'Vsaj en sodnik', step: 'judges' },
  ];
  const ready = checks.every((c) => c.ok);
  const mutation = competition.status === CompetitionStatus.Draft ? schedule : activate;
  const nextLabel =
    competition.status === CompetitionStatus.Draft
      ? 'Načrtuj tekmovanje'
      : competition.status === CompetitionStatus.Scheduled
        ? 'Aktiviraj (odpri oddajo)'
        : null;

  return (
    <Card>
      <CardBody className="flex max-w-2xl flex-col gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-fg-secondary">Trenutno stanje:</span>
          <Badge tone={competitionStatusTone(competition.status)}>{competitionStatusLabel(competition.status)}</Badge>
        </div>
        <ul className="flex flex-col gap-2">
          {checks.map((c) => (
            <li key={c.label} className="flex items-center gap-2 text-sm">
              <span className={c.ok ? 'text-success' : 'text-fg-muted'}>
                {c.ok ? <Check size={16} aria-hidden="true" /> : '○'}
              </span>
              <span className={c.ok ? 'text-fg' : 'text-fg-secondary'}>{c.label}</span>
              {!c.ok && (
                <button type="button" className="text-accent-text underline" onClick={() => goTo(c.step)}>
                  Uredi
                </button>
              )}
            </li>
          ))}
        </ul>
        <p className="text-sm text-fg-muted">
          Kotizacija je v koraku Osnovno, podatki kluba za plačila pa v meniju pri »Plačila«. Nagrade lahko dodate tudi pozneje, do konca ocenjevanja.
        </p>
        {mutation.isError && <Alert variant="error">{getApiErrorMessage(mutation.error)}</Alert>}
        {nextLabel ? (
          <Button
            className="self-start"
            disabled={!ready}
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate(competition.id)}
          >
            {nextLabel}
          </Button>
        ) : (
          <p className="text-sm text-fg-secondary">
            Nadaljnje korake (pregled, ocenjevanje, podelitev nagrad) vodite na strani tekmovanja.{' '}
            <Link to={`/competitions/${competition.slug}`} className="font-medium text-accent-text underline">
              Odpri tekmovanje
            </Link>
          </p>
        )}
      </CardBody>
    </Card>
  );
}

/** Competition setup as a step-by-step wizard, in the order an organizer prepares a competition. */
export function OrganizerSetupPage() {
  const { competitionId } = useCompetitionIdParam();
  const competition = useCompetition(competitionId);
  const [params, setParams] = useSearchParams();
  const requested = params.get('step');
  const index = Math.max(0, STEPS.findIndex((s) => s.key === requested));
  const current = STEPS[index];

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [index]);

  const goTo = (key: StepKey) => setParams({ step: key });

  if (competition.isLoading) return <PageSpinner />;
  if (competition.isError || !competition.data) {
    return <ErrorState message={getApiErrorMessage(competition.error)} onRetry={() => competition.refetch()} />;
  }
  const c = competition.data;

  return (
    <div className="flex flex-col gap-5">
      <CompetitionWorkspaceHeader competitionId={competitionId} title="Nastavitve" />

      <nav aria-label="Koraki nastavitve" className="flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => goTo(s.key)}
            aria-current={i === index ? 'step' : undefined}
            className={`flex items-center gap-2 rounded-control border px-3 py-1.5 text-sm ${
              i === index ? 'border-accent bg-accent-subtle text-fg' : 'border-line text-fg-secondary hover:bg-surface-2'
            }`}
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-xs text-fg">
              {i + 1}
            </span>
            {s.label}
          </button>
        ))}
      </nav>

      <h2 className="text-lg font-semibold text-fg">
        {index + 1}. {current.label}
      </h2>

      {current.key === 'basics' && <BasicsStep competition={c} />}
      {current.key === 'categories' && <OrganizerCategoriesPage embedded />}
      {current.key === 'announcement' && <OrganizerLandingPage embedded />}
      {current.key === 'judges' && <OrganizerJudgesPage embedded />}
      {current.key === 'awards' && <OrganizerAwardsPage embedded />}
      {current.key === 'publish' && <PublishStep competition={c} goTo={goTo} />}

      <div className="flex justify-between border-t border-line pt-4">
        <Button variant="secondary" disabled={index === 0} onClick={() => goTo(STEPS[index - 1].key)}>
          Nazaj
        </Button>
        {index < STEPS.length - 1 && <Button onClick={() => goTo(STEPS[index + 1].key)}>Naprej</Button>}
      </div>
    </div>
  );
}
